import { UILinkButton, UIRoute, UIView } from "uicore-ts"

import { RHAltModeKeyable, RHAltModeKeyHandler } from "./Keyboard navigation/RHAltModeKeyHandler"
import { RHBarButton, RHBarButtonVariant } from "./RHBarButton"


// MARK: - RH Bar Link Button

export class RHBarLinkButton extends UILinkButton implements RHAltModeKeyable {
    
    declare button: RHBarButton
    primaryActionWasTriggered?: (button: RHBarLinkButton, event?: Event) => void | Promise<void>
    
    constructor(elementID?: string) {
        
        super(elementID, "button")
        
        this.button.removeFromSuperview()
        this.button = new RHBarButton(this.elementID + "Button")
        this.addSubview(this.button)
        
        // noinspection JSConstantReassignment
        this.button.controlEventTargetAccumulator.PrimaryActionTriggered = async (_sender: UIView, event?: Event) => {
            if (this.primaryActionWasTriggered) {
                await this.primaryActionWasTriggered(this, event)
                return
            }

            const targetRoute = this.targetRouteForCurrentState()
            if (targetRoute instanceof UIRoute) {
                const routeWasApplied = await targetRoute.apply()
                if (routeWasApplied) {
                    this.sendControlEventForKey(UILinkButton.controlEvent.PrimaryActionTriggered, event)
                }
                return
            }

            window.location.href = targetRoute
            this.sendControlEventForKey(UILinkButton.controlEvent.PrimaryActionTriggered, event)
        }

    }


    override updateTarget() {
        const route = this.targetRouteForCurrentState()
        if (route instanceof UIRoute) {
            this.viewHTMLElement.removeAttribute("href")
            return
        }

        this.target = route
    }


    set variant(value: RHBarButtonVariant) {
        this.button.variant = value
    }
    
    get variant(): RHBarButtonVariant {
        return this.button.variant
    }
    
    
    override set selected(value: boolean) {
        this.button.selected = value
    }
    
    override get selected(): boolean {
        return this.button.selected
    }
    
    
    // MARK: - RHAltModeKeyable
    
    /**
     * Forwards to the inner button's handler. RHBarLinkButton does not own a
     * handler directly — it owns the inner RHBarButton which holds it.
     */
    get _altModeKeyHandler(): RHAltModeKeyHandler | undefined {
        return this.button._altModeKeyHandler
    }
    
    set _altModeKeyHandler(value: RHAltModeKeyHandler | undefined) {
        this.button._altModeKeyHandler = value
    }
    
    /**
     * Registers a keyboard shortcut for this link button. The badge is drawn
     * on the link-button wrapper (this view) so it is positioned correctly
     * over the full clickable area.
     */
    get altModeKey(): string | undefined {
        return this.button.altModeKey
    }
    
    set altModeKey(key: string | undefined) {
        this._setAltModeKey(key, key ? this : undefined)
    }
    
    _setAltModeKey(key: string | undefined, displayView: UIView | undefined) {
        this.button._setAltModeKey(key, displayView)
    }
    
}
